import { Flex } from 'antd';
import { FC, ReactNode, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { DownloadButton } from '@/pages/UserAccount/UserRequests/ui';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import { ArtifactType, InvoiceStatus, UserRequestStatus } from '@/shared/types';
import {
  ButtonSizes,
  ButtonVariant,
  ConditionView,
  Icon,
  IconName,
  LinkAsButton,
  StepControlPanel,
  Typography,
} from '@/shared/ui';

import { iconByStatus } from '@/components/Forms/Steps/Result';

import { useArtifactLoader } from '@/hooks/useArtifactLoader';
import { Breakpoints, useMediaQuery } from '@/hooks/useMediaQuery';
import { useShortPolling } from '@/hooks/useShortPolling';

import { ExtendedCriminalRecordExtractSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';

import { ROUTES, ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { ColorConfig } from '@/contexts/AntdConfigProvider/theme';

import { DeliveryType } from '@/lib/consts';

import api from '@/api';

import { ExtractFormType } from '../../lib/constants';

import './ResultStep.scss';

type CriminalRecordExtractResultMessage = {
  title: string;
  primaryText: string;
  deadlineText?: string;
  icon?: { name: IconName; color: ColorConfig };
};

type CriminalRecordExtractResult = {
  message: CriminalRecordExtractResultMessage;
  buttons: ReactNode;
};

export interface ResultStepProps {
  serviceCode: string;
  userRequestId: string;
  invoiceStatus: InvoiceStatus;
  selectedExtractFormType: ExtractFormType;
  willBePaidLater?: boolean;
}

export const ResultStep: FC<StepProps<ResultStepProps>> = ({
  serviceCode,
  userRequestId,
  invoiceStatus,
  selectedExtractFormType,
  willBePaidLater,
}) => {
  const isMobileSize = useMediaQuery(Breakpoints.SM);
  const { control } = useFormContext<ExtendedCriminalRecordExtractSchema>();
  const deliveryType = useWatch({
    control,
    name: 'deliveryType',
  });
  const hasApostille = useWatch({
    control,
    name: 'hasApostille',
  });
  const { isArtifactLoading, currentArtifactTypeLoading, downloadArtifact } = useArtifactLoader((artifactType) =>
    api.downloadUserRequestArtifact(userRequestId, artifactType),
  );

  const {
    isShortPollingSuccessed: isUserRequestFinished,
    isShortPollingFailed,
    startShortPolling: checkIsUserRequestFinished,
  } = useShortPolling({
    delayTime: 1000 * 15,
    totalAwaitTime: 1000 * 180,
    fetchFunction: async () => {
      const { data: userRequest } = await api.services.draft.getDetails(userRequestId);
      return userRequest;
    },
    isResponseAppropriate: ({ artifacts, status }) => {
      return status === UserRequestStatus.FINISHED || !!artifacts?.includes(ArtifactType.EXTRACT);
    },
  });

  useEffect(() => {
    invoiceStatus === InvoiceStatus.SUCCESS && checkIsUserRequestFinished();
  }, [invoiceStatus]);

  const resultForElectronic = useMemo<CriminalRecordExtractResult>(() => {
    if (isShortPollingFailed) {
      return {
        message: {
          icon: iconByStatus['new'][InvoiceStatus.FAILED],
          title: 'Ваш запит перебуває в обробці',
          primaryText:
            'Термін обробки запиту - до 30 календарних днів. На обробку потрібно більше часу, результат ви отримаєте в меню “Мої запити”.',
        },
        buttons: (
          <>
            <LinkAsButton
              to={ROUTES.USER.REQUESTS.ACTIVE_REQUESTS}
              style={{ width: isMobileSize ? '100%' : 'fit-content' }}
              variant={ButtonVariant.SECONDARY}
            >
              Мої запити
            </LinkAsButton>
            <LinkAsButton
              to={ROUTES.HOME}
              style={{ width: isMobileSize ? '100%' : 'fit-content' }}
              className="extended-criminal-record-result__button--right-side"
            >
              На головну
            </LinkAsButton>
          </>
        ),
      };
    }

    if (!isUserRequestFinished) {
      return {
        message: {
          icon: iconByStatus['new'][InvoiceStatus.IN_PROGRESS],
          title: 'Зачекайте, будь ласка, ваш запит обробляється',
          primaryText: 'Орієнтований час обробки – 2 хвилини',
        },
        buttons: (
          <>
            <LinkAsButton
              to={ROUTES.USER.REQUESTS.ACTIVE_REQUESTS}
              style={{ width: isMobileSize ? '100%' : 'fit-content' }}
              variant={ButtonVariant.SECONDARY}
            >
              Мої запити
            </LinkAsButton>
            <LinkAsButton
              to={ROUTES.HOME}
              style={{ width: isMobileSize ? '100%' : 'fit-content' }}
              className="extended-criminal-record-result__button--right-side"
            >
              На головну
            </LinkAsButton>
          </>
        ),
      };
    }

    return {
      message: {
        icon: iconByStatus['new'][InvoiceStatus.SUCCESS],
        title: 'Ваш запит успішно оброблено',
        primaryText:
          'Ви можете скачати свій витяг. Перевірити статус опрацювання запиту на витяг можна у розділі "Мої запити".',
      },
      buttons: (
        <>
          <DownloadButton
            size={ButtonSizes.LG}
            serviceCode={serviceCode}
            iconName="download"
            buttonVariant={ButtonVariant.SECONDARY}
            type={ArtifactType.SIGN}
            disabled={isArtifactLoading && currentArtifactTypeLoading === ArtifactType.SIGN}
            onClick={() => downloadArtifact(ArtifactType.SIGN, serviceCode)}
          />
          <DownloadButton
            size={ButtonSizes.LG}
            serviceCode={serviceCode}
            iconName="download"
            buttonVariant={ButtonVariant.SECONDARY}
            type={ArtifactType.EXTRACT}
            disabled={isArtifactLoading && currentArtifactTypeLoading === ArtifactType.EXTRACT}
            onClick={() => downloadArtifact(ArtifactType.EXTRACT, serviceCode)}
          />
          <LinkAsButton
            to={ROUTES.HOME}
            style={{ width: isMobileSize ? '100%' : 'fit-content' }}
            className="extended-criminal-record-result__button--right-side"
          >
            На головну
          </LinkAsButton>
        </>
      ),
    };
  }, [isUserRequestFinished, isShortPollingFailed, isMobileSize]);

  const resultForPaper = useMemo<CriminalRecordExtractResult>(() => {
    const getDelayedPaymentResult = (invoiceStatusForIcon: InvoiceStatus) => ({
      message: {
        icon: iconByStatus['new'][invoiceStatusForIcon],
        title: 'Оплату не завершено',
        deadlineText:
          'Вам необхідно оплатити послугу надання дозвольного документа впродовж 14 днів, після закінчення цього терміну запит буде анульовано',
        primaryText:
          'Ви можете завершити оплату зараз, натиснувши на кнопку “Оплатити” або у будь-який зручний час, відкривши чернетку цього запиту в розділі “Мої запити”',
      },
      buttons: (
        <>
          <LinkAsButton
            to={ROUTES.USER.REQUESTS.ACTIVE_REQUESTS}
            style={{ width: isMobileSize ? '100%' : 'fit-content' }}
            variant={ButtonVariant.SECONDARY}
          >
            Мої запити
          </LinkAsButton>
          <LinkAsButton
            to={ROUTES_BY_SERVICE_CODE[serviceCode]}
            state={{
              continuePayment: true,
              request_id: userRequestId,
            }}
            style={{ width: isMobileSize ? '100%' : 'fit-content' }}
          >
            Оплатити
          </LinkAsButton>
        </>
      ),
    });

    if (willBePaidLater) {
      return getDelayedPaymentResult(InvoiceStatus.IN_PROGRESS);
    }

    switch (invoiceStatus) {
      case InvoiceStatus.FAILED:
        return getDelayedPaymentResult(InvoiceStatus.FAILED);
      default:
        return {
          message: {
            icon: iconByStatus['new'][InvoiceStatus.SUCCESS],
            title: 'Ваш запит успішно зареєстровано',
            primaryText:
              'Орієнтовний термін опрацювання запиту до 10 робочих днів. У разі потреби проведення додаткових перевірок, термін опрацювання може бути збільшено до 30 робочих днів. Перевірити статус опрацювання запиту на витяг можна у розділі "Мої запити".',
          },
          buttons: (
            <>
              {(hasApostille || deliveryType?.id !== DeliveryType.PERSONAL) && (
                <DownloadButton
                  size={ButtonSizes.LG}
                  serviceCode={serviceCode}
                  iconName="download"
                  buttonVariant={ButtonVariant.SECONDARY}
                  type={ArtifactType.RECEIPT}
                  disabled={isArtifactLoading && currentArtifactTypeLoading === ArtifactType.RECEIPT}
                  onClick={() => downloadArtifact(ArtifactType.RECEIPT, serviceCode)}
                />
              )}
              <LinkAsButton
                to={ROUTES.USER.REQUESTS.ACTIVE_REQUESTS}
                style={{ width: isMobileSize ? '100%' : 'fit-content' }}
                className="extended-criminal-record-result__button--right-side"
              >
                Мої запити
              </LinkAsButton>
            </>
          ),
        };
    }
  }, [invoiceStatus, willBePaidLater, isMobileSize]);

  const { message, buttons } =
    selectedExtractFormType === ExtractFormType.ELECTRONIC ? resultForElectronic : resultForPaper;

  return (
    <>
      <FormBlock title="Результат">
        <Flex vertical gap={16}>
          <ConditionView if={!!message.icon}>
            <Icon name={message.icon!.name} color={message.icon!.color} width={40} height={40} />
          </ConditionView>

          <Flex vertical gap={8}>
            <Typography variant="H3">{message.title}</Typography>
            <Typography>{message.primaryText}</Typography>
            <ConditionView if={!!message.deadlineText}>
              <Typography>{message!.deadlineText}</Typography>
            </ConditionView>
          </Flex>
        </Flex>
      </FormBlock>
      <StepControlPanel>
        <Flex justify="space-between" vertical={isMobileSize} gap={8} align="center">
          <Flex gap={8} vertical={isMobileSize} className="extended-criminal-record-result__buttons">
            {buttons}
          </Flex>
        </Flex>
      </StepControlPanel>
    </>
  );
};
